import React, { useState, useEffect } from "react";
import { Search, Filter, MapPin, ChevronLeft, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function NewPrograms() {
  const navigate = useNavigate();

  const [showFilter, setShowFilter] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    location: "",
  });

  // ✅ FIXED - Duration helper now includes both start and end dates
  const getProgramDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";
    const start = new Date(startDate);
    const end = new Date(endDate);
    // ✅ FIXED: Added + 1 to include both start and end dates
    const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    if (diffDays < 30) return `${diffDays} Days`;
    const months = Math.floor(diffDays / 30);
    return `${months} Month${months > 1 ? "s" : ""}`;
  };

  // 🔹 Calculate discounted price based on discount type
  const calculateDiscountedPrice = (schedule, originalPrice) => {
    // Return original price if no schedule or discount not applicable
    if (
      !schedule ||
      !schedule.discount_applicable ||
      !schedule.discount_value
    ) {
      return Number(originalPrice) || 0;
    }

    const discountType = schedule.discount_type;
    const discountValue = Number(schedule.discount_value) || 0;
    const price = Number(originalPrice) || 0;

    // Check if discount is currently valid
    if (schedule.discount_valid_from && schedule.discount_valid_to) {
      const now = new Date();
      const validFrom = new Date(schedule.discount_valid_from);
      const validTo = new Date(schedule.discount_valid_to);

      if (now < validFrom || now > validTo) {
        return price; // Discount period has not started or has ended
      }
    }

    if (discountType === "Percentage") {
      // Percentage discount: reduce by percentage
      const discountAmount = (price * discountValue) / 100;
      return price - discountAmount;
    } else if (discountType === "FlatAmount") {
      // Flat amount discount: subtract directly
      return Math.max(0, price - discountValue); // Don't go below 0
    }

    return price;
  };

  // 🔥 Fetch courses (with filters)
  const fetchCourses = async (appliedFilters = {}) => {
    try {
      setLoading(true);

      const params = {
        title: search || undefined,
        start_date_from: appliedFilters.dateFrom || undefined,
        start_date_to: appliedFilters.dateTo || undefined,
        location: appliedFilters.location || undefined,
      };

      const response = await axios.get(`${API_URL}/courses/`, { params });
      const data = response.data?.data || [];

      const formattedCourses = data.flatMap((c) => {
        if (!c.schedules || c.schedules.length === 0) return [];

        return c.schedules.map((schedule) => {
          const originalPrice = Number(c.amount) || 0;
          const discountedPrice = calculateDiscountedPrice(
            schedule,
            originalPrice,
          );

          return {
            id: `${c.course_id}-${schedule.schedule_id}`, // unique key
            courseId: c.course_id,
            scheduleId: schedule.schedule_id,

            title: c.title,
            location: schedule.location || "N/A",
            originalPrice: originalPrice,
            discountedPrice: discountedPrice,
            hasDiscount: schedule.discount_applicable || false,

            modules: c.total_modules ?? 0,
            duration: getProgramDuration(
              schedule.start_date,
              schedule.end_date,
            ),

            startDate: schedule.start_date
              ? new Date(schedule.start_date).toLocaleDateString("en-US", {
                  day: "2-digit",
                  month: "short",
                })
              : "N/A",

            image:
              c.thumbnail_url ||
              c.program_thumbnail ||
              "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400",
          };
        });
      });

      setCourses(formattedCourses);
    } catch (error) {
      console.error("Error fetching courses:", error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // 🧹 Clear filters
  const handleClearAll = () => {
    const cleared = { dateFrom: "", dateTo: "", location: "" };
    setFilters(cleared);
    setSearch("");
    fetchCourses(cleared);
  };

  // ✅ Apply filters
  const handleConfirm = () => {
    setShowFilter(false);
    fetchCourses(filters);
  };
  const today = new Date();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100 p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/myprograms")}
          className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-full"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold">Add New Programs</h1>
      </div>

      {/* Search + Filter */}
      <div className="flex gap-4 mb-8">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search course name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchCourses(filters)}
            className="w-full h-11 px-4 pr-10 border rounded"
          />
          <Search className="absolute right-3 top-3 w-5 h-5" />
        </div>

        <button
          onClick={() => setShowFilter(true)}
          className="h-11 px-6 border rounded flex items-center gap-2 bg-white"
        >
          <Filter className="w-4 h-4" />
          Filter
        </button>
      </div>

      {/* Course Grid */}
      {courses.length === 0 ? (
        <p className="text-center text-gray-500">No programs found</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {courses.map((course) => (
            <div
              key={course.id}
              className="bg-white rounded shadow cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() =>
                navigate(`/courses/${course.courseId}`, {
                  state: { scheduleId: course.scheduleId },
                })
              }
            >
              <img
                src={course.image}
                alt={course.title}
                className="h-52 w-full object-cover rounded-t"
              />
              <div className="p-4">
                <div className="text-xs text-teal-500 mb-2 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {course.location}
                </div>
                <h3 className="font-semibold mb-2 line-clamp-2">
                  {course.title}
                </h3>

                <div className="text-sm mb-2">
                  {course.hasDiscount &&
                  course.discountedPrice < course.originalPrice ? (
                    <>
                      <span className="line-through text-red-400 mr-2">
                        ${course.originalPrice.toFixed(2)}
                      </span>
                      <span className="font-semibold text-green-600">
                        ${course.discountedPrice.toFixed(2)}
                      </span>
                    </>
                  ) : (
                    <span className="font-semibold">
                      ${course.originalPrice.toFixed(2)}
                    </span>
                  )}
                </div>

                <div className="text-xs text-gray-600 border-t pt-2">
                  {course.modules} Modules | {course.duration} | Start:{" "}
                  {course.startDate}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filter Modal */}
      {/* Filter Modal */}
      {showFilter && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowFilter(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded w-full max-w-sm z-50">
            <div className="flex justify-between mb-4">
              <h2 className="font-semibold">Filters</h2>
              <button onClick={() => setShowFilter(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <DatePicker
                selected={filters.dateFrom ? new Date(filters.dateFrom) : null}
                minDate={today} // ✅ cannot select past date
                onChange={(date) =>
                  setFilters({
                    ...filters,
                    dateFrom: date
                      ? `${date.getFullYear()}-${(date.getMonth() + 1)
                          .toString()
                          .padStart(2, "0")}-${date
                          .getDate()
                          .toString()
                          .padStart(2, "0")}`
                      : "",
                    dateTo: "", // ✅ reset dateTo when dateFrom changes
                  })
                }
                placeholderText="MM/DD/YYYY"
                className="border p-2 rounded w-full"
              />

              <DatePicker
                selected={filters.dateTo ? new Date(filters.dateTo) : null}
                minDate={filters.dateFrom ? new Date(filters.dateFrom) : today}
                onChange={(date) =>
                  setFilters({
                    ...filters,
                    dateTo: date
                      ? `${date.getFullYear()}-${(date.getMonth() + 1)
                          .toString()
                          .padStart(2, "0")}-${date
                          .getDate()
                          .toString()
                          .padStart(2, "0")}`
                      : "",
                  })
                }
                placeholderText="MM/DD/YYYY"
                className="border p-2 rounded w-full"
              />
            </div>

            <input
              type="text"
              placeholder="Location"
              value={filters.location}
              onChange={(e) =>
                setFilters({ ...filters, location: e.target.value })
              }
              className="border p-2 rounded w-full mb-4"
            />

            <div className="flex gap-3">
              <button
                onClick={handleClearAll}
                className="flex-1 border rounded py-2"
              >
                Clear
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 bg-teal-500 text-white rounded py-2"
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
